import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const path = res.user.role === "admin" ? "/admin" : res.user.role === "seller" ? "/vendeur" : "/acheteur";
      navigate(path);
    } else setError(res.error);
  };

  return (
    <div className="container-page py-16 flex justify-center">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-white ring-1 ring-border items-center justify-center">
            <img src={brand.logoUrl} alt={brand.name} className="h-12 w-12 object-contain" />
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-3xl text-secondary">{t("auth.loginTitle")}</h1>
          <p className="text-muted-foreground mt-1">{t("auth.loginSubtitle")}</p>
        </div>

        <form onSubmit={submit} className="rounded-2xl bg-card border border-border p-6 space-y-4" data-testid="login-form">
          <div>
            <Label>{t("auth.identifier")}</Label>
            <Input type="text" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="email@exemple.com ou +223…" data-testid="login-email" />
          </div>
          <div>
            <Label>{t("auth.password")}</Label>
            <div className="relative mt-1.5">
              <Input type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-11" data-testid="login-password" />
              <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-login-password" aria-label="toggle">
                {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <Link to="/mot-de-passe-oublie" className="mt-1.5 inline-block text-sm text-primary font-semibold hover:underline" data-testid="forgot-password-link">{t("auth.forgotPassword")}</Link>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-primary/90 h-11" data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
            {t("auth.loginBtn")}
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            {t("auth.noAccount")}{" "}
            <Link to="/inscription" className="text-primary font-semibold hover:underline" data-testid="go-register">{t("nav.register")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
